import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Trash2, Wand2, Sparkles } from 'lucide-react';
import { ProductForm } from '../product/ProductForm';
import { BulkActionBar } from '../BulkActionBar';
import { CollapsibleSection } from './CollapsibleSection';
import { DeleteConfirmationModal } from '../common/DeleteConfirmationModal';
import { ImagePopup } from '../folders/ImagePopup';
import { useProductStore } from '../../stores/productStore';
import { generateTitleFromImages, generateTitleAndCategoryFromImages } from '../../utils/openai';
import { useAuthStore } from '../../stores/authStore';
import { useAiCredits } from '../../hooks/useAiCredits';
import { AIResponsePopup } from '../notifications/AIResponsePopup';
import { MobileProgressMessage } from '../notifications/MobileProgressMessage';
import { useAIResponse } from '../../hooks/useAIResponse';
import { InsufficientCreditsModal } from '../notifications/InsufficientCreditsModal';
import { BulkAIConfirmation } from '../bulk/BulkAIConfirmation';
import { BulkTitleAndCategoryConfirmation } from '../bulk/BulkTitleAndCategoryConfirmation';
import { useAiCreditsStore } from '../../stores/aiCreditsStore';
import type { Product } from '../../types/product';
import type { BulkUpdates } from '../../types/bulk';
import { useFolderStore } from '../../stores/folderStore';
import {
  AI_TITLE_GENERATION_CREDIT_COST,
  AI_TITLE_AND_CATEGORY_CREDIT_COST,
} from '../../utils/aiCredits/costs';

const PRODUCTS_PER_PAGE = 200;

interface ProductListProps {
  products: Product[];
  onUpdate: (product: Product) => Promise<void>;
  onDelete: (productId: number) => void;
  onBulkUpdate: (updates: BulkUpdates, selectedIds: number[]) => void;
}

export function ProductList({
  products,
  onUpdate,
  onDelete,
  onBulkUpdate
}: ProductListProps) {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showAIConfirmation, setShowAIConfirmation] = useState(false);
  const [showTitleAndCategoryConfirmation, setShowTitleAndCategoryConfirmation] = useState(false);
  const [isBulkActionsOpen, setIsBulkActionsOpen] = useState(false);
  const [expandedProductId, setExpandedProductId] = useState<number | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentProductIndex, setCurrentProductIndex] = useState(0);
  const [bulkTargetCount, setBulkTargetCount] = useState(0);
  const [bulkProgressMode, setBulkProgressMode] = useState<'title' | 'titleAndCategory'>('title');
  const [lastSelectedId, setLastSelectedId] = useState<number | null>(null);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const { isVisible, message, showResponse, hideResponse } = useAIResponse();
  const { chargedCredits, planCredits } = useAiCredits();
  const { loadCredits } = useAiCreditsStore();
  const toggleProductSelection = useProductStore(state => state.toggleProductSelection);
  const selectAllProducts = useProductStore(state => state.selectAllProducts);
  const clearProductSelections = useProductStore(state => state.clearProductSelections);
  const currentFolderId = useFolderStore(state => state.currentFolderId);
  const [currentPage, setCurrentPage] = useState(1);
  const isMobile = window.innerWidth < 768;
  const cancelRef = React.useRef(false);

  // Image preview state
  const [previewProductIndex, setPreviewProductIndex] = useState<number | null>(null);
  const [previewImageIndex, setPreviewImageIndex] = useState<number | null>(null);
  const productRefs = useRef<{[key: number]: HTMLDivElement | null}>({});

  const selectedProducts = products.filter(p => p.isSelected);
  const selectedProductsWithImages = selectedProducts.filter(p => p.images.length > 0);
  const totalAvailableCredits = planCredits + chargedCredits;
  const hasCreditsForTitle = totalAvailableCredits >= AI_TITLE_GENERATION_CREDIT_COST;
  const hasCreditsForTitleAndCategory = (count: number) =>
    totalAvailableCredits >= count * AI_TITLE_AND_CATEGORY_CREDIT_COST;
  const totalPages = Math.max(1, Math.ceil(products.length / PRODUCTS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * PRODUCTS_PER_PAGE;
  const paginatedProducts = products.slice(pageStart, pageStart + PRODUCTS_PER_PAGE);

  // Clear all selections when switching folders (selectAllProducts(false) only clears current folder)
  useEffect(() => {
    clearProductSelections();
    setCurrentPage(1);
    setLastSelectedId(null);
  }, [currentFolderId, clearProductSelections]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSelectAll = useCallback((selected: boolean) => {
    selectAllProducts(selected);
  }, [selectAllProducts]);

  const handleProductSelect = useCallback((productId: number, selected: boolean, event?: React.MouseEvent) => {
    if (event?.shiftKey && lastSelectedId !== null) {
      // Find indices of last selected and current product
      const lastIndex = products.findIndex(p => p.id === lastSelectedId);
      const currentIndex = products.findIndex(p => p.id === productId);
      
      if (lastIndex !== -1 && currentIndex !== -1) {
        // Determine range start and end
        const start = Math.min(lastIndex, currentIndex);
        const end = Math.max(lastIndex, currentIndex);
        
        // Get current selection state of the clicked item
        const targetState = !products[currentIndex].isSelected;
        
        // Select all products in range to match the target state
        for (let i = start; i <= end; i++) {
          const product = products[i];
          if (product.isSelected !== targetState) {
            toggleProductSelection(product.id);
          }
        }
      }
    } else {
      // Regular click - toggle selection and update last selected
      toggleProductSelection(productId);
      setLastSelectedId(productId);
    }
  }, [products, toggleProductSelection, lastSelectedId]);

  const handleBulkDelete = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmBulkDelete = async () => {
    await Promise.all(selectedProducts.map(p => onDelete(p.id)));
    setShowDeleteConfirmation(false);
  };

  const handleBulkUpdate = (updates: BulkUpdates) => {
    if (selectedProducts.length > 0) {
      onBulkUpdate(updates, selectedProducts.map(p => p.id));
      if (expandedProductId && selectedProducts.some(p => p.id === expandedProductId)) {
        setExpandedProductId(null);
      }
    }
  };

  const handleExpand = (productId: number) => {
    setExpandedProductId(productId === expandedProductId ? null : productId);
  };

  const getLatestProduct = (product: Product): Product => {
    return useProductStore.getState().products.find(p => p.id === product.id) ?? product;
  };

  const handleBulkCreditError = (error: unknown) => {
    if (error instanceof Error && error.message.includes('AIクレジットが不足')) {
      setShowCreditsModal(true);
      cancelRef.current = true;
    }
  };

  const generateTitleForProduct = async (product: Product): Promise<void> => {
    if (!product.images.length) return;

    const user = useAuthStore.getState().user;
    if (!user?.id) {
      throw new Error('ユーザー認証が必要です');
    }

    try {
      const generatedTitle = await generateTitleFromImages(
        product.images,
        user.id,
        product.category
      );
      if (generatedTitle) {
        const latestProduct = getLatestProduct(product);
        const updatedProduct = { ...latestProduct, title: generatedTitle };
        await onUpdate(updatedProduct);
        showResponse(`商品: ${generatedTitle}`);
      }
    } catch (error) {
      handleBulkCreditError(error);
      if (error instanceof Error) {
        showResponse(error.message);
      }
    }
  };

  const generateTitleAndCategoryForProduct = async (product: Product): Promise<void> => {
    if (!product.images.length) return;

    const user = useAuthStore.getState().user;
    if (!user?.id) {
      throw new Error('ユーザー認証が必要です');
    }

    try {
      const latestProduct = getLatestProduct(product);
      const result = await generateTitleAndCategoryFromImages(
        latestProduct.images,
        user.id,
        latestProduct.category
      );

      const updatedProduct = {
        ...latestProduct,
        title: result.title,
        ...(result.categoryResolved
          ? { category: result.category, categoryYahoo: result.categoryYahoo }
          : {})
      };
      await onUpdate(updatedProduct);

      const categoryNote = result.categoryResolved
        ? ` / カテゴリ: ${result.category}`
        : ' / カテゴリ: 自動判定できず';
      showResponse(`商品: ${result.title}${categoryNote}`);
    } catch (error) {
      handleBulkCreditError(error);
      if (error instanceof Error) {
        showResponse(error.message);
      }
    }
  };

  const runBulkGeneration = async (
    processor: (product: Product) => Promise<void>,
    targets: Product[],
    progressMode: 'title' | 'titleAndCategory'
  ) => {
    if (targets.length === 0) return;

    setBulkTargetCount(targets.length);
    setBulkProgressMode(progressMode);
    setIsGenerating(true);
    cancelRef.current = false;

    try {
      const CONCURRENCY_LIMIT = isMobile ? 2 : 5;
      let currentIndex = 0;

      async function processBatch() {
        const batch = [];
        for (let i = 0; i < CONCURRENCY_LIMIT && currentIndex < targets.length; i++, currentIndex++) {
          if (cancelRef.current) break;
          setCurrentProductIndex(currentIndex);
          batch.push(processor(targets[currentIndex]));
        }
        await Promise.allSettled(batch);
      }

      while (currentIndex < targets.length && !cancelRef.current) {
        await processBatch();
        if (!cancelRef.current && currentIndex < targets.length) {
          await new Promise(resolve => setTimeout(resolve, isMobile ? 1000 : 100));
        }
      }
    } catch (error) {
      handleBulkCreditError(error);
      if (error instanceof Error) {
        showResponse(error.message);
      }
    } finally {
      setIsGenerating(false);
      setCurrentProductIndex(0);
      setBulkTargetCount(0);
      cancelRef.current = false;

      try {
        await loadCredits();
      } catch (error) {
        console.error('Failed to reload AI credits:', error);
      }
    }
  };

  const ensureBulkCredits = (targetCount: number, creditsPerProduct = 1): boolean => {
    if (targetCount === 0 || isGenerating) return false;
    const requiredCredits = targetCount * creditsPerProduct;
    if (totalAvailableCredits < requiredCredits) {
      setShowCreditsModal(true);
      return false;
    }
    return true;
  };

  const handleBulkAIGenerate = () => {
    if (!ensureBulkCredits(selectedProductsWithImages.length)) return;
    setShowAIConfirmation(true);
  };

  const handleConfirmAIGenerate = async () => {
    setShowAIConfirmation(false);
    const targets = selectedProductsWithImages;
    await runBulkGeneration(generateTitleForProduct, targets, 'title');
  };

  const handleBulkTitleAndCategoryGenerate = () => {
    if (!ensureBulkCredits(selectedProductsWithImages.length, AI_TITLE_AND_CATEGORY_CREDIT_COST)) return;
    setShowTitleAndCategoryConfirmation(true);
  };

  const handleConfirmBulkTitleAndCategoryGenerate = async () => {
    setShowTitleAndCategoryConfirmation(false);
    const targets = selectedProductsWithImages;
    await runBulkGeneration(generateTitleAndCategoryForProduct, targets, 'titleAndCategory');
  };

  const handleCancel = () => {
    cancelRef.current = true;
  };

  // Image preview handlers
  const handleOpenPreview = (productIndex: number, imageIndex: number) => {
    setPreviewProductIndex(productIndex);
    setPreviewImageIndex(imageIndex);
  };

  const handleClosePreview = () => {
    setPreviewProductIndex(null);
    setPreviewImageIndex(null);
  };

  const handlePreviousProduct = () => {
    if (previewProductIndex === null || previewProductIndex <= 0) return;
    setPreviewProductIndex(previewProductIndex - 1);
    setPreviewImageIndex(0);
  };

  const handleNextProduct = () => {
    if (previewProductIndex === null || previewProductIndex >= products.length - 1) return;
    setPreviewProductIndex(previewProductIndex + 1);
    setPreviewImageIndex(0);
  };

  // Function to scroll to the product
  const scrollToProduct = (index: number) => {
    const productRef = productRefs.current[index];
    if (productRef) {
      setTimeout(() => {
        productRef.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center'
        });
      }, 100); // Small delay to ensure the UI has updated
    }
  };

  // Keep the list on the page that contains the previewed product (pagination only renders one page)
  useEffect(() => {
    if (previewProductIndex === null) return;
    const pageForIndex = Math.floor(previewProductIndex / PRODUCTS_PER_PAGE) + 1;
    setCurrentPage(prev => (prev !== pageForIndex ? pageForIndex : prev));
  }, [previewProductIndex]);

  useEffect(() => {
    if (previewProductIndex === null) return;
    scrollToProduct(previewProductIndex);
  }, [previewProductIndex, safePage]);

  // Handler for updating title from preview modal
  const handleTitleChange = (newTitle: string) => {
    if (previewProductIndex === null) return;
    
    const product = products[previewProductIndex];
    if (product) {
      const updatedProduct = { ...product, title: newTitle };
      onUpdate(updatedProduct);
    }
  };

  return (
    <div className="">
      {/* Mobile Progress Message */}
      <MobileProgressMessage
        isVisible={isGenerating && isMobile}
        currentIndex={currentProductIndex}
        totalCount={bulkTargetCount}
        mode={bulkProgressMode}
      />

      {products.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-3 sm:p-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
            <label className="flex items-center shrink-0">
              <input
                type="checkbox"
                checked={selectedProducts.length === products.length && products.length > 0}
                onChange={(e) => handleSelectAll(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                disabled={products.length === 0}
              />
              <span className="ml-2 text-sm text-gray-700 whitespace-nowrap">
                すべて選択 ({selectedProducts.length}/{products.length})
              </span>
            </label>
            {selectedProducts.length > 0 && (
              <div className="flex w-full flex-col gap-2 lg:w-auto lg:flex-row lg:flex-nowrap lg:items-center lg:justify-end lg:gap-2 xl:gap-3">
                <button
                  onClick={handleBulkAIGenerate}
                  disabled={!hasCreditsForTitle || isGenerating || selectedProductsWithImages.length === 0}
                  className={`btn-ai-compact flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-2 text-sm whitespace-nowrap ${
                    isGenerating ? 'animate-pulse' : ''
                  }`}
                >
                  <Wand2 className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                  <span>AIでまとめて生成</span>
                </button>
                <button
                  onClick={handleBulkTitleAndCategoryGenerate}
                  disabled={
                    !hasCreditsForTitleAndCategory(selectedProductsWithImages.length) ||
                    isGenerating ||
                    selectedProductsWithImages.length === 0
                  }
                  className={`flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-gradient-to-r from-[#2563EB] via-[#0EA5E9] to-[#22D3EE] hover:from-blue-700 hover:via-sky-600 hover:to-cyan-500 whitespace-nowrap ${
                    isGenerating ? 'animate-pulse' : ''
                  }`}
                >
                  <Sparkles className={`w-4 h-4 ${isGenerating ? 'animate-spin' : ''}`} />
                  <span>タイトルとカテゴリ生成</span>
                </button>
                <button
                  onClick={() => setIsBulkActionsOpen(!isBulkActionsOpen)}
                  className="flex w-full lg:w-auto items-center justify-center px-3 py-2 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md border border-blue-100 lg:border-transparent lg:px-2 lg:py-1.5 whitespace-nowrap"
                >
                  選択された項目を変更
                </button>
                <button
                  onClick={handleBulkDelete}
                  className="flex w-full lg:w-auto items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md border border-red-100 lg:border-transparent whitespace-nowrap"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>選択した商品を削除</span>
                </button>
              </div>
            )}
          </div>
          {selectedProducts.length > 0 && (
            <CollapsibleSection
              title="選択された項目を変更"
              isOpen={isBulkActionsOpen}
              onToggle={() => setIsBulkActionsOpen(!isBulkActionsOpen)}
              className="border-t border-gray-200"
            >
              <BulkActionBar 
                onUpdate={handleBulkUpdate}
                selectedCount={selectedProducts.length}
              />
            </CollapsibleSection>
          )}
        </div>
      )}

      <div className="space-y-6 mt-6">
        {paginatedProducts.map((product, index) => {
          const globalIndex = pageStart + index;
          return (
            <div
              key={product.id}
              ref={el => { productRefs.current[globalIndex] = el; }}
              id={`product-${product.id}`}
            >
              <ProductForm
                product={product}
                onUpdate={onUpdate}
                onDelete={onDelete}
                isSelected={product.isSelected}
                onSelect={(selected, event) => handleProductSelect(product.id, selected, event)}
                isExpanded={expandedProductId === product.id}
                onExpand={() => handleExpand(product.id)}
                isGeneratingBulk={isGenerating}
                onOpenPreview={(imageIndex) => handleOpenPreview(globalIndex, imageIndex)}
              />
            </div>
          );
        })}
      </div>

      {products.length > PRODUCTS_PER_PAGE && (
        <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-3 bg-white rounded-lg border border-gray-200 px-4 py-3">
          <p className="text-sm text-gray-600">
            {pageStart + 1}–{Math.min(pageStart + PRODUCTS_PER_PAGE, products.length)}件 / 全{products.length}件
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              前へ
            </button>
            <span className="text-sm text-gray-700 min-w-[4.5rem] text-center">
              {safePage} / {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              次へ
            </button>
          </div>
        </div>
      )}

      {showDeleteConfirmation && (
        <DeleteConfirmationModal
          onConfirm={handleConfirmBulkDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          message={`選択した${selectedProducts.length}件の商品を本当に削除しますか？この操作は元に戻せません。`}
        />
      )}

      {showAIConfirmation && (
        <BulkAIConfirmation
          selectedCount={selectedProductsWithImages.length}
          onConfirm={handleConfirmAIGenerate}
          onCancel={() => setShowAIConfirmation(false)}
        />
      )}

      {showTitleAndCategoryConfirmation && (
        <BulkTitleAndCategoryConfirmation
          selectedCount={selectedProductsWithImages.length}
          onConfirm={handleConfirmBulkTitleAndCategoryGenerate}
          onCancel={() => setShowTitleAndCategoryConfirmation(false)}
        />
      )}

      {previewProductIndex !== null && previewImageIndex !== null && products[previewProductIndex] && (
        <ImagePopup
          images={products[previewProductIndex].images}
          currentIndex={previewImageIndex}
          title={products[previewProductIndex].title}
          onClose={handleClosePreview}
          onPrevious={() => setPreviewImageIndex(Math.max(0, previewImageIndex - 1))}
          onNext={() => setPreviewImageIndex(Math.min(products[previewProductIndex].images.length - 1, previewImageIndex + 1))}
          onPreviousProduct={previewProductIndex > 0 ? handlePreviousProduct : undefined}
          onNextProduct={previewProductIndex < products.length - 1 ? handleNextProduct : undefined}
          onTitleChange={handleTitleChange}
        />
      )}

      {showCreditsModal && (
        <InsufficientCreditsModal onClose={() => setShowCreditsModal(false)} />
      )}

      <AIResponsePopup
        message={message}
        isVisible={isVisible}
        onClose={hideResponse}
      />
    </div>
  );
}